import { useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { ArrowLeftIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface ProductDetail {
  id: string
  code: string
  name: string
  sgtCodes: string[] | null
  cargoTypeId: string | null
  cargoTypeName: string | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

function fmtDate(v: string) {
  return new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

export default function CargoProductDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [editOpen,   setEditOpen]   = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form,       setForm]       = useState({ code: "", name: "", cargoTypeId: "" })
  const [sgtCodes,   setSgtCodes]   = useState<string[]>([])
  const [sgtInput,   setSgtInput]   = useState("")
  const [formError,  setFormError]  = useState<string | null>(null)

  const { data: cargoTypesList = [] } = useQuery({
    queryKey: ["cargos", "types"],
    queryFn: async () => {
      const res = await api.cargos.types.$get({ query: { deleted: "false" } })
      return res.ok ? (res.json() as Promise<{ id: string; code: string; name: string }[]>) : []
    },
    staleTime: 5 * 60 * 1000,
  })

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["cargo-product", id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/cargos/products/${id}`)
      if (!res.ok) throw new Error("Producto no encontrado")
      return res.json() as Promise<ProductDetail>
    },
    enabled: !!id,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateMut = useMutation({
    mutationFn: async (data: { code: string; name: string; sgtCodes: string[] | null; cargoTypeId: string | null }) => {
      const res = await authorizedFetch(`${API_URL}/api/cargos/products/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar")
      }
      return res.json() as Promise<ProductDetail>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargo-product", id] })
      qc.invalidateQueries({ queryKey: ["cargos", "products"] })
      setEditOpen(false)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/cargos/products/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargo-product", id] })
      qc.invalidateQueries({ queryKey: ["cargos", "products"] })
      setDeleteOpen(false)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/cargos/products/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargo-product", id] })
      qc.invalidateQueries({ queryKey: ["cargos", "products"] })
    },
    onError: (err: Error) => setFormError(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  function openEdit() {
    if (!product) return
    setForm({ code: product.code, name: product.name, cargoTypeId: product.cargoTypeId ?? "" })
    setSgtCodes(product.sgtCodes ?? [])
    setSgtInput("")
    setFormError(null)
    setEditOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.code.trim() || !form.name.trim()) {
      setFormError("Código y nombre son requeridos")
      return
    }
    updateMut.mutate({
      code:        form.code.trim().toUpperCase(),
      name:        form.name.trim(),
      sgtCodes:    sgtCodes.length ? sgtCodes : null,
      cargoTypeId: form.cargoTypeId || null,
    })
  }

  // ── Loading / error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Producto" />
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !product) {
    return (
      <>
        <SiteHeader title="Producto no encontrado" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-sm text-muted-foreground">No se encontró el producto.</p>
          <Button variant="outline" onClick={() => navigate("/cargos/products")}>
            <ArrowLeftIcon className="size-4 mr-2" /> Volver a productos
          </Button>
        </div>
      </>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title={product.name} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-2xl space-y-6">

          {/* Barra superior */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <ArrowLeftIcon className="size-4" /> Volver
              </button>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
                {product.code}
              </span>
              {product.deletedAt && (
                <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                  Eliminado
                </Badge>
              )}
            </div>

            {can("write", "cargo") && (
              <div className="flex items-center gap-2">
                {product.deletedAt ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => restoreMut.mutate()}
                    disabled={restoreMut.isPending}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    <RestoreIcon className="size-4 mr-1.5" />
                    {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={openEdit}>
                      <PencilIcon className="size-4 mr-1.5" /> Editar
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setDeleteOpen(true)}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2Icon className="size-4 mr-1.5" /> Eliminar
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Operador también puede editar */}
            {!can("write", "cargo") && !product.deletedAt && (
              <Button variant="outline" size="sm" onClick={openEdit}>
                <PencilIcon className="size-4 mr-1.5" /> Editar
              </Button>
            )}
          </div>

          {/* Datos */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificación
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Código" value={product.code} mono />
              <Field label="Nombre" value={product.name} />
              <div className="col-span-2 flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Tipo de carga</dt>
                <dd className="text-sm">
                  {product.cargoTypeName
                    ? <span className="bg-muted px-2 py-0.5 rounded text-xs">{product.cargoTypeName}</span>
                    : <span className="text-muted-foreground/50">—</span>}
                </dd>
              </div>
            </dl>
          </div>

          {/* Códigos SGT */}
          <div className="rounded-lg border p-5 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Códigos SGT (SmartGT)
            </h3>
            {product.sgtCodes?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {product.sgtCodes.map((s) => (
                  <span key={s} className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{s}</span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground/50">Sin códigos SGT asignados</p>
            )}
          </div>

          {/* Registro */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Registro
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Creado"      value={fmtDate(product.createdAt)} />
              <Field label="Actualizado" value={fmtDate(product.updatedAt)} />
              {product.deletedAt && (
                <Field label="Eliminado" value={fmtDate(product.deletedAt)} />
              )}
            </dl>
          </div>

        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setFormError(null) } }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>Editar producto</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            <div className="grid gap-1.5">
              <Label htmlFor="pd-code">Código</Label>
              <Input
                id="pd-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={10}
                className="font-mono uppercase"
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="pd-name">Nombre</Label>
              <Input
                id="pd-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Códigos SGT</Label>
              <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
                {sgtCodes.map((code, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                    {code}
                    <button
                      type="button"
                      onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={sgtInput}
                  onChange={(e) => setSgtInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      const code = sgtInput.toUpperCase().trim()
                      if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                      setSgtInput("")
                    }
                  }}
                  placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                  className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Se normalizan automáticamente.</p>
            </div>

            {/* Tipo de carga */}
            <div className="grid gap-1.5">
              <Label>Tipo de carga</Label>
              <Select
                value={form.cargoTypeId || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, cargoTypeId: v === "__none__" ? "" : (v ?? "") }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin tipo">
                    {form.cargoTypeId
                      ? (cargoTypesList.find((t) => t.id === form.cargoTypeId)?.name ?? form.cargoTypeId)
                      : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin tipo</SelectItem>
                  {cargoTypesList.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending ? "Guardando..." : "Guardar cambios"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar producto</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{product.code}</strong> — {product.name}? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
